import { FormTypes, MiscTypes } from '@oneblink/types'
import { v4 } from 'uuid'

import {
  findElement,
  replaceElement,
  updateOptions,
} from '../definition-manipulations'
import {
  findMeasures,
  getVisibleSubCarriers,
  ProcessedMeasuresAndRequirements,
  processMeasures,
} from '../measures-and-requirements'
import {
  filterEnteringNSWOriginDetailsOptions,
  filterEnteringNSWDestinationOptions,
  filterWithinNSWDestinationOptions,
  filterPropertyDestinationOptions,
  filterMovementOriginatingOptions,
} from '../origin-details-and-destination-options'

export type UpdateElementOptions = {
  readOnly?: boolean
  isDataLookup?: boolean
}

export default function updateDefinition({
  originalDefinition,
  currentDefinition,
  submission,
  referenceData,
  userProfile,
  isOffline,
}: {
  originalDefinition: FormTypes.Form
  currentDefinition: FormTypes.Form
  submission: CTRomSubmission
  referenceData: ReferenceData[]
  userProfile: MiscTypes.UserProfile | null
  isOffline: boolean
}) {
  const movementOriginatingElementName = 'Movement_Originating'
  const originalMovementOriginatingElement = findElement(
    originalDefinition,
    movementOriginatingElementName,
  )

  if (
    originalMovementOriginatingElement?.value &&
    originalMovementOriginatingElement?.value.type === 'select'
  ) {
    const options = filterMovementOriginatingOptions(
      originalMovementOriginatingElement.value.options ?? [],
      submission,
    )

    updateOptions({
      definition: currentDefinition,
      elementName: movementOriginatingElementName,
      options,
    })
  }

  const driversName = findElement(currentDefinition, 'Drivers_name')

  if (driversName?.value.type === 'select' && driversName.parent.elements) {
    const driversNameOptions: FormTypes.ChoiceElementOption[] = []
    const otherOption = driversName.value?.options?.find(
      (o) => o.value.toLowerCase() === 'other',
    )

    if (
      !submission.isDPIOfficer__exclude__hidden &&
      userProfile &&
      userProfile?.fullName
    ) {
      driversNameOptions.push({
        id: v4(),
        label: userProfile.fullName,
        value: userProfile.fullName,
      })
    }

    if (submission.Stock_Owner_First_Name && submission.Stock_Owner_Last_Name) {
      const stockOwnerFullName = `${submission.Stock_Owner_First_Name} ${submission.Stock_Owner_Last_Name}`
      driversNameOptions.push({
        id: v4(),
        label: stockOwnerFullName,
        value: stockOwnerFullName,
      })
    }

    if (otherOption) {
      driversNameOptions.push(otherOption)
    }

    driversName.parent.elements = replaceElement(
      driversName.parent.elements,
      driversName.value,
      { ...driversName.value, options: driversNameOptions },
    )
  }

  const measuresData = referenceData?.find(
    (d) => d.type === 'requirementsAndMeasures',
  )

  let newMeasuresAndRequirements: ProcessedMeasuresAndRequirements | undefined
  if (measuresData && measuresData.type === 'requirementsAndMeasures') {
    const measures = findMeasures(
      submission,
      measuresData.value,
      currentDefinition,
    )
    const selectedAnimals = (submission.Cattle_Tick_Carrier || []).concat(
      getVisibleSubCarriers(submission, currentDefinition),
    )

    newMeasuresAndRequirements = processMeasures(measures, selectedAnimals)
    console.log(
      'New measures and requirements after processing',
      newMeasuresAndRequirements,
    )
  }

  const originalOriginDetails = findElement(
    originalDefinition,
    'Origin_Details',
  )

  const liveOriginDetails = findElement(currentDefinition, 'Origin_Details')

  const originalFinalDestination = findElement(
    originalDefinition,
    'Final_Destination',
  )

  const liveFinalDestination = findElement(
    currentDefinition,
    'Final_Destination',
  )

  const originalDestinationDetails = findElement(
    originalDefinition,
    'Destination_Details',
  )

  const liveDestinationDetails = findElement(
    currentDefinition,
    'Destination_Details',
  )

  if (submission.Entering_NSW !== undefined) {
    let originDetailsOptions
    let finalDestinationOptions

    //origin
    if (originalOriginDetails?.value.type === 'select') {
      if (
        submission.Entering_NSW === 'Yes' &&
        originalOriginDetails.value.options
      ) {
        originDetailsOptions = filterEnteringNSWOriginDetailsOptions(
          originalOriginDetails.value.options,
          submission,
        )
      }

      if (
        liveOriginDetails?.value.type === 'select' &&
        liveOriginDetails.parent.elements &&
        originDetailsOptions
      ) {
        liveOriginDetails.parent.elements = replaceElement(
          liveOriginDetails.parent.elements,
          liveOriginDetails.value,
          {
            ...liveOriginDetails.value,
            options: originDetailsOptions.length
              ? [...originDetailsOptions]
              : originalOriginDetails.value.options,
          },
        )
      }
    }

    // destination
    if (originalFinalDestination?.value.type === 'select') {
      if (
        submission.Entering_NSW === 'Yes' &&
        originalFinalDestination.value.options
      ) {
        finalDestinationOptions = filterEnteringNSWDestinationOptions(
          originalFinalDestination.value.options,
          submission,
        )
      } else if (
        submission.Entering_NSW === 'No' &&
        originalFinalDestination.value.options
      ) {
        finalDestinationOptions = filterWithinNSWDestinationOptions(
          originalFinalDestination.value.options,
          submission,
        )
      }

      if (
        liveFinalDestination?.value.type === 'select' &&
        liveFinalDestination.parent.elements &&
        finalDestinationOptions
      ) {
        liveFinalDestination.parent.elements = replaceElement(
          liveFinalDestination.parent.elements,
          liveFinalDestination.value,
          {
            ...liveFinalDestination.value,
            options: finalDestinationOptions.length
              ? [...finalDestinationOptions]
              : originalFinalDestination.value.options,
          },
        )
      }

      if (
        submission.Final_Destination === 'To a property' &&
        originalDestinationDetails?.value.type === 'select' &&
        originalDestinationDetails.value.options
      ) {
        const destinationDetailsOptions = filterPropertyDestinationOptions(
          originalDestinationDetails.value.options,
          submission,
        )

        if (
          liveDestinationDetails?.value.type === 'select' &&
          liveDestinationDetails.parent.elements &&
          destinationDetailsOptions
        ) {
          liveDestinationDetails.parent.elements = replaceElement(
            liveDestinationDetails.parent.elements,
            liveDestinationDetails.value,
            {
              ...liveDestinationDetails.value,
              options: destinationDetailsOptions.length
                ? [...destinationDetailsOptions]
                : originalDestinationDetails.value.options,
            },
          )
        }
      }
    }
  }

  if (submission.Origin_Stock_Owner === 'No') {
    // if above is "yes", this field is never shown
    const liveWhoIsDeclaring = findElement(currentDefinition, 'whoIsDeclaring')

    const opts: FormTypes.ChoiceElementOption[] = []

    if (userProfile) {
      const loggedInUser = `${userProfile.firstName || ''} ${
        userProfile.lastName || ''
      }`
      opts.push({
        id: v4(),
        label: loggedInUser,
        value: loggedInUser,
      })
    }
    if (submission.Stock_Owner_First_Name && submission.Stock_Owner_Last_Name) {
      const stockOwnerFullName = `${submission.Stock_Owner_First_Name} ${submission.Stock_Owner_Last_Name}`
      opts.push({
        id: v4(),
        label: stockOwnerFullName,
        value: stockOwnerFullName,
      })
    }

    if (
      liveWhoIsDeclaring &&
      liveWhoIsDeclaring.parent.elements &&
      liveWhoIsDeclaring.value.type === 'select'
    ) {
      liveWhoIsDeclaring.parent.elements = replaceElement(
        liveWhoIsDeclaring.parent.elements,
        liveWhoIsDeclaring.value,
        {
          ...liveWhoIsDeclaring.value,
          options: opts,
        },
      )
    }
  }

  const destinationPropertyAddress = findElement<FormTypes.PointAddressElement>(
    currentDefinition,
    'Destination_Property_Address',
  )

  if (destinationPropertyAddress?.value) {
    destinationPropertyAddress.parent.elements = replaceElement(
      destinationPropertyAddress.parent.elements || [],
      destinationPropertyAddress.value,
      {
        ...destinationPropertyAddress.value,
        readOnly: isOffline,
      },
    )
  }

  const dippingAddress = findElement<FormTypes.PointAddressElement>(
    currentDefinition,
    'Dipping_Address',
  )
  if (dippingAddress?.value) {
    dippingAddress.parent.elements = replaceElement(
      dippingAddress.parent.elements || [],
      dippingAddress.value,
      {
        ...dippingAddress.value,
        readOnly: isOffline,
      },
    )
  }

  const qldAddress = findElement<FormTypes.PointAddressElement>(
    currentDefinition,
    'AddressQLD',
  )
  if (qldAddress?.value) {
    qldAddress.parent.elements = replaceElement(
      qldAddress.parent.elements || [],
      qldAddress.value,
      {
        ...qldAddress.value,
        readOnly: isOffline,
      },
    )
  }

  const nswAddress = findElement<FormTypes.PointAddressElement>(
    currentDefinition,
    'AddressNSW',
  )
  if (nswAddress?.value) {
    nswAddress.parent.elements = replaceElement(
      nswAddress.parent.elements || [],
      nswAddress.value,
      {
        ...nswAddress.value,
        readOnly: isOffline,
      },
    )
  }

  //need to do this to rebuild the validation functions
  currentDefinition.elements = [...currentDefinition.elements]

  return currentDefinition
}
